/**
 * Session Service for QA AI Agent
 *
 * Handles chat session management API calls
 */

import {
  ChatSession,
  ChatSessionListResponse,
  ChatSessionWithMessages,
} from '../types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8003/api/v1';

/**
 * List chat sessions for current user
 */
export const listSessions = async (
  projectId?: string,
  search?: string,
  page: number = 1,
  pageSize: number = 50
): Promise<ChatSessionListResponse> => {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  if (projectId) params.append('project_id', projectId);
  if (search) params.append('search', search);

  const response = await fetch(`${API_URL}/sessions?${params.toString()}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to list sessions');
  }

  return response.json();
};

/**
 * Create a new chat session
 */
export const createSession = async (
  title?: string,
  projectId?: string
): Promise<ChatSession> => {
  const response = await fetch(`${API_URL}/sessions`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      title,
      project_id: projectId,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to create session');
  }

  return response.json();
};

/**
 * Get a chat session with its messages
 */
export const getSession = async (
  sessionId: string,
  messageLimit: number = 100
): Promise<ChatSessionWithMessages> => {
  const response = await fetch(
    `${API_URL}/sessions/${sessionId}?message_limit=${messageLimit}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to get session');
  }

  return response.json();
};

/**
 * Update a chat session
 */
export const updateSession = async (
  sessionId: string,
  updates: { title?: string; summary?: string; is_active?: boolean }
): Promise<ChatSession> => {
  const response = await fetch(`${API_URL}/sessions/${sessionId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to update session');
  }

  return response.json();
};

/**
 * Delete a chat session
 */
export const deleteSession = async (sessionId: string): Promise<void> => {
  const response = await fetch(`${API_URL}/sessions/${sessionId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to delete session');
  }
};

const sessionService = {
  listSessions,
  createSession,
  getSession,
  updateSession,
  deleteSession,
};

export default sessionService;
